import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  AutoComplete,
  DatePicker,
  InputNumber,
  Progress,
  Tag,
  Spin,
  Empty,
  Row,
  Col,
  Popconfirm,
} from 'antd';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import useApiMethods from '../../lib/useApiMethods';
import { BUDGETS, BUDGET_OVERVIEW } from '../../constants/api';
import ToastMsg from '../../components/common/ToastMsg';

const BUDGET_DEPARTMENTS = '/api/finance/budgets/departments/';

const formatINR = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);

const BUDGET_CATEGORIES = '/api/finance/budgets/categories/';

const getProgressColor = (percent) => {
  if (percent > 90) return '#ef4444';
  if (percent >= 70) return '#f59e0b';
  return '#10b981';
};

const getStatusTag = (statusValue) => {
  const map = {
    on_track: { color: 'green', label: 'On Track' },
    warning: { color: 'orange', label: 'Warning' },
    over_budget: { color: 'red', label: 'Over Budget' },
  };
  const s = map[statusValue] || { color: 'default', label: statusValue };
  return <Tag color={s.color}>{s.label}</Tag>;
};

const BudgetActualsTab = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'));
  const [deptFilter, setDeptFilter] = useState(undefined);
  const [overview, setOverview] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [budgetDetail, setBudgetDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modals
  const [addBudgetOpen, setAddBudgetOpen] = useState(false);
  const [addBudgetSubmitting, setAddBudgetSubmitting] = useState(false);
  const [addBudgetForm] = Form.useForm();

  const [addActualOpen, setAddActualOpen] = useState(false);
  const [addActualSubmitting, setAddActualSubmitting] = useState(false);
  const [addActualForm] = Form.useForm();

  const [editActualOpen, setEditActualOpen] = useState(false);
  const [editActualSubmitting, setEditActualSubmitting] = useState(false);
  const [editActualForm] = Form.useForm();
  const [editingActual, setEditingActual] = useState(null);

  const [editBudgetOpen, setEditBudgetOpen] = useState(false);
  const [editBudgetSubmitting, setEditBudgetSubmitting] = useState(false);
  const [editBudgetForm] = Form.useForm();

  const [deptSuggestions, setDeptSuggestions] = useState([]);
  const [catSuggestions, setCatSuggestions] = useState([]);

  const { fetch, create, update, remove } = useApiMethods();

  // ------------------------------------------------------------------
  // Data loading
  // ------------------------------------------------------------------

  const loadDepartments = async () => {
    const res = await fetch(BUDGET_DEPARTMENTS);
    if (res?.status === 'success') {
      setDeptSuggestions((res.data || []).map((d) => ({ value: d, label: d })));
    }
  };

  const loadCategories = async () => {
    const res = await fetch(BUDGET_CATEGORIES);
    if (res?.status === 'success') {
      setCatSuggestions((res.data || []).map((c) => ({ value: c, label: c })));
    }
  };

  const loadOverview = useCallback(async () => {
    const monthStr = selectedMonth.format('YYYY-MM-01');
    const res = await fetch(BUDGET_OVERVIEW, { month: monthStr }, setOverviewLoading);
    if (res?.status !== 'fail') {
      const d = res?.data;
      setOverview(Array.isArray(d) ? d : d?.results || []);
    }
  }, [selectedMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBudgetDetail = useCallback(async (budgetId) => {
    const res = await fetch(`${BUDGETS}${budgetId}/`, {}, setDetailLoading);
    if (res?.status !== 'fail') {
      setBudgetDetail(res?.data || null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadOverview();
    loadDepartments();
    loadCategories();
  }, [loadOverview]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedBudget) {
      loadBudgetDetail(selectedBudget);
    } else {
      setBudgetDetail(null);
    }
  }, [selectedBudget, loadBudgetDetail]);

  // ------------------------------------------------------------------
  // Edit Budget
  // ------------------------------------------------------------------

  const openEditBudget = () => {
    if (!budgetDetail) return;
    editBudgetForm.setFieldsValue({
      allocated_amount: parseFloat(budgetDetail.allocated_amount),
    });
    setEditBudgetOpen(true);
  };

  const handleEditBudget = async (values) => {
    if (!selectedBudget) return;
    setEditBudgetSubmitting(true);
    try {
      const res = await update(`${BUDGETS}${selectedBudget}/`, {
        allocated_amount: String(values.allocated_amount),
      });
      if (res?.status !== 'fail') {
        ToastMsg('success', 'Budget updated successfully');
        setEditBudgetOpen(false);
        editBudgetForm.resetFields();
        loadBudgetDetail(selectedBudget);
        loadOverview();
      } else {
        ToastMsg('error', res?.message || 'Failed to update budget');
      }
    } catch (error) {
      ToastMsg('error', 'Failed to update budget');
    } finally {
      setEditBudgetSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Add Budget
  // ------------------------------------------------------------------

  const handleAddBudget = async (values) => {
    setAddBudgetSubmitting(true);
    try {
      const payload = {
        department: values.department,
        month: values.month.format('YYYY-MM-01'),
        allocated_amount: values.allocated_amount,
      };
      const res = await create(BUDGETS, payload);
      if (res?.status !== 'fail') {
        ToastMsg('success', 'Budget created successfully');
        setAddBudgetOpen(false);
        addBudgetForm.resetFields();
        loadOverview();
        loadDepartments();
      } else {
        ToastMsg('error', res?.message || 'Failed to create budget');
      }
    } catch (error) {
      ToastMsg('error', 'Failed to create budget');
    } finally {
      setAddBudgetSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Record Actual
  // ------------------------------------------------------------------

  const handleAddActual = async (values) => {
    if (!selectedBudget) return;
    setAddActualSubmitting(true);
    try {
      const payload = {
        category: values.category,
        amount: String(values.amount),
        description: values.description || '',
        actual_date: values.actual_date ? values.actual_date.format('YYYY-MM-DD') : null,
        receipt_url: values.receipt_url || '',
      };
      const url = `${BUDGETS}${selectedBudget}/actuals/`;
      const res = await create(url, payload);
      if (res?.status !== 'fail') {
        ToastMsg('success', 'Actual recorded successfully');
        setAddActualOpen(false);
        addActualForm.resetFields();
        loadBudgetDetail(selectedBudget);
        loadOverview();
        loadCategories();
      } else {
        ToastMsg('error', res?.message || 'Failed to record actual');
      }
    } catch (error) {
      ToastMsg('error', 'Failed to record actual');
    } finally {
      setAddActualSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Edit Actual
  // ------------------------------------------------------------------

  const openEditActual = (actual) => {
    setEditingActual(actual);
    editActualForm.setFieldsValue({
      category: actual.category,
      amount: parseFloat(actual.amount),
      description: actual.description || '',
      actual_date: actual.actual_date ? dayjs(actual.actual_date) : null,
      receipt_url: actual.receipt_url || '',
    });
    setEditActualOpen(true);
  };

  const handleEditActual = async (values) => {
    if (!editingActual) return;
    setEditActualSubmitting(true);
    try {
      const payload = {
        category: values.category,
        amount: values.amount,
        description: values.description || '',
        actual_date: values.actual_date.format('YYYY-MM-DD'),
        receipt_url: values.receipt_url || '',
      };
      const res = await update(`${BUDGETS}actuals/${editingActual.id}/`, payload);
      if (res?.status !== 'fail') {
        ToastMsg('success', 'Actual updated successfully');
        setEditActualOpen(false);
        editActualForm.resetFields();
        setEditingActual(null);
        loadBudgetDetail(selectedBudget);
        loadOverview();
      } else {
        ToastMsg('error', res?.message || 'Failed to update actual');
      }
    } catch (error) {
      ToastMsg('error', 'Failed to update actual');
    } finally {
      setEditActualSubmitting(false);
    }
  };

  // ------------------------------------------------------------------
  // Delete Actual
  // ------------------------------------------------------------------

  const handleDeleteActual = async (actualId) => {
    try {
      const res = await remove(`${BUDGETS}actuals/${actualId}/`);
      if (res?.status !== 'fail') {
        ToastMsg('success', 'Actual deleted');
        loadBudgetDetail(selectedBudget);
        loadOverview();
      } else {
        ToastMsg('error', res?.message || 'Failed to delete actual');
      }
    } catch (error) {
      ToastMsg('error', 'Failed to delete actual');
    }
  };

  // ------------------------------------------------------------------
  // Filtered overview
  // ------------------------------------------------------------------

  const filteredOverview = deptFilter
    ? overview.filter((o) => o.department === deptFilter)
    : overview;

  // ------------------------------------------------------------------
  // Actuals table columns
  // ------------------------------------------------------------------

  const actualsColumns = [
    {
      title: 'Date',
      dataIndex: 'actual_date',
      key: 'actual_date',
      render: (val) => (val ? dayjs(val).format('DD MMM YYYY') : '-'),
      sorter: (a, b) => dayjs(a.actual_date).unix() - dayjs(b.actual_date).unix(),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (val) => val || '-',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (val) => val || '-',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (val) => formatINR(val),
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
    },
    {
      title: 'Recorded By',
      dataIndex: 'recorded_by',
      key: 'recorded_by',
      render: (val) => val?.name || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <Button
            type="text"
            size="small"
            icon={<Edit2 className="w-3.5 h-3.5" />}
            onClick={() => openEditActual(record)}
          />
          <Popconfirm
            title="Delete this actual?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteActual(record.id)}
            okText="Delete"
            okType="danger"
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<Trash2 className="w-3.5 h-3.5" />}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Top section: filters + actions */}
      <Card className="shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <DatePicker
            picker="month"
            value={selectedMonth}
            onChange={(val) => {
              setSelectedMonth(val || dayjs().startOf('month'));
              setSelectedBudget(null);
            }}
            allowClear={false}
            className="w-48"
          />

          <AutoComplete
            placeholder="Filter by department"
            allowClear
            className="w-48"
            value={deptFilter}
            onChange={(val) => setDeptFilter(val || undefined)}
            options={deptSuggestions}
          />

          <div className="ml-auto">
            <Button
              type="primary"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => {
                addBudgetForm.setFieldsValue({ month: selectedMonth });
                setAddBudgetOpen(true);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 border-none"
            >
              Add Budget
            </Button>
          </div>
        </div>
      </Card>

      {/* Overview cards */}
      {overviewLoading ? (
        <div className="flex justify-center py-16">
          <Spin size="large" />
        </div>
      ) : filteredOverview.length === 0 ? (
        <Card className="shadow-sm">
          <Empty description={`No budgets set for ${selectedMonth.format('MMMM YYYY')}`} />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {filteredOverview.map((item) => {
            const percent = item.percent_used || 0;
            const isSelected = selectedBudget === item.id;
            return (
              <Col xs={24} sm={12} lg={8} key={item.id || item.department}>
                <Card
                  hoverable
                  className={`shadow-sm cursor-pointer transition-all ${
                    isSelected ? 'ring-2 ring-indigo-500 border-indigo-500' : ''
                  }`}
                  onClick={() =>
                    setSelectedBudget(isSelected ? null : item.id)
                  }
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-semibold text-foreground text-base">
                      {item.department}
                    </span>
                    {getStatusTag(item.status)}
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <div className="text-xs text-muted-foreground">Allocated</div>
                      <div className="font-medium text-foreground">
                        {formatINR(item.allocated)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Spent</div>
                      <div className="font-medium text-foreground">
                        {formatINR(item.spent)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Remaining</div>
                      <div
                        className={`font-medium ${
                          parseFloat(item.remaining) < 0
                            ? 'text-red-600'
                            : 'text-foreground'
                        }`}
                      >
                        {formatINR(item.remaining)}
                      </div>
                    </div>
                  </div>

                  <Progress
                    percent={Math.min(Math.round(percent), 100)}
                    strokeColor={getProgressColor(percent)}
                    size="small"
                    format={() => `${percent}%`}
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Budget Detail section */}
      {selectedBudget && (
        <Card
          className="shadow-sm"
          title={
            budgetDetail ? (
              <span>
                {budgetDetail.department} -{' '}
                {budgetDetail.month
                  ? dayjs(budgetDetail.month).format('MMMM YYYY')
                  : ''}
              </span>
            ) : (
              'Budget Detail'
            )
          }
          extra={
            <div className="flex gap-2">
              <Popconfirm
                title="Delete this budget?"
                description="All actuals under this budget will also be deleted."
                onConfirm={async () => {
                  const res = await remove(`${BUDGETS}${selectedBudget}/`, {});
                  if (res?.status !== 'fail') {
                    ToastMsg('success', 'Budget deleted');
                    setSelectedBudget(null);
                    setBudgetDetail(null);
                    loadOverview();
                  } else {
                    ToastMsg('error', res?.message || 'Failed to delete');
                  }
                }}
                okText="Delete"
                okType="danger"
              >
                <Button danger icon={<Trash2 className="w-4 h-4" />}>
                  Delete
                </Button>
              </Popconfirm>
              <Button
                icon={<Edit2 className="w-4 h-4" />}
                onClick={openEditBudget}
              >
                Edit Budget
              </Button>
              <Button
                type="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setAddActualOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 border-none"
              >
                Record Actual
              </Button>
            </div>
          }
        >
          {detailLoading ? (
            <div className="flex justify-center py-12">
              <Spin size="large" />
            </div>
          ) : !budgetDetail ? (
            <Empty description="Could not load budget details" />
          ) : (
            <>
              {/* Summary stats */}
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={8}>
                  <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                    <div className="text-sm text-muted-foreground">Allocated</div>
                    <div className="text-xl font-bold text-foreground">
                      {formatINR(budgetDetail.allocated_amount)}
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div className="text-center p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                    <div className="text-sm text-muted-foreground">Total Actuals</div>
                    <div className="text-xl font-bold text-foreground">
                      {formatINR(budgetDetail.total_actuals)}
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <div
                    className={`text-center p-4 rounded-lg ${
                      parseFloat(budgetDetail.remaining) < 0
                        ? 'bg-red-50 dark:bg-red-950/30'
                        : 'bg-green-50 dark:bg-green-950/30'
                    }`}
                  >
                    <div className="text-sm text-muted-foreground">Remaining</div>
                    <div
                      className={`text-xl font-bold ${
                        parseFloat(budgetDetail.remaining) < 0
                          ? 'text-red-600'
                          : 'text-green-700 dark:text-green-300'
                      }`}
                    >
                      {formatINR(budgetDetail.remaining)}
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Actuals table */}
              <Table
                dataSource={budgetDetail.actuals || []}
                columns={actualsColumns}
                rowKey={(record) => record.id}
                pagination={{ pageSize: 10 }}
                locale={{
                  emptyText: (
                    <Empty description="No actuals recorded yet. Click 'Record Actual' to add one." />
                  ),
                }}
                summary={(pageData) => {
                  if (!pageData || pageData.length === 0) return null;
                  const totalOnPage = pageData.reduce(
                    (sum, row) => sum + parseFloat(row.amount || 0),
                    0
                  );
                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row className="bg-muted font-medium">
                        <Table.Summary.Cell index={0} colSpan={3}>
                          Total (this page)
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          {formatINR(totalOnPage)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} colSpan={2} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </>
          )}
        </Card>
      )}

      {/* ============================================================= */}
      {/* Edit Budget Modal */}
      {/* ============================================================= */}
      <Modal
        title="Edit Budget"
        open={editBudgetOpen}
        onCancel={() => {
          setEditBudgetOpen(false);
          editBudgetForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={editBudgetForm}
          layout="vertical"
          onFinish={handleEditBudget}
        >
          <Form.Item
            name="allocated_amount"
            label="Allocated Amount"
            rules={[{ required: true, message: 'Please enter the allocated amount' }]}
          >
            <InputNumber
              className="w-full"
              min={0}
              formatter={(value) =>
                `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              }
              parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={editBudgetSubmitting}
              block
              className="bg-indigo-600 hover:bg-indigo-700 border-none"
            >
              Update Budget
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ============================================================= */}
      {/* Add Budget Modal */}
      {/* ============================================================= */}
      <Modal
        title="Add Budget"
        open={addBudgetOpen}
        onCancel={() => {
          setAddBudgetOpen(false);
          addBudgetForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={addBudgetForm}
          layout="vertical"
          onFinish={handleAddBudget}
        >
          <Form.Item
            name="department"
            label="Department"
            rules={[{ required: true, message: 'Enter a department name' }]}
          >
            <AutoComplete
              placeholder="Type or select department"
              options={deptSuggestions}
              filterOption={(input, option) =>
                (option?.value || '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="month"
            label="Month"
            rules={[{ required: true, message: 'Please select a month' }]}
          >
            <DatePicker picker="month" className="w-full" />
          </Form.Item>

          <Form.Item
            name="allocated_amount"
            label="Allocated Amount"
            rules={[
              { required: true, message: 'Please enter the allocated amount' },
            ]}
          >
            <InputNumber
              className="w-full"
              min={0}
              step={10000}
              prefix="₹"
              placeholder="Enter budget amount"
              formatter={(value) =>
                value
                  ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  : ''
              }
              parser={(value) => value.replace(/,/g, '')}
            />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Button
              onClick={() => {
                setAddBudgetOpen(false);
                addBudgetForm.resetFields();
              }}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={addBudgetSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 border-none"
            >
              Create Budget
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ============================================================= */}
      {/* Record Actual Modal */}
      {/* ============================================================= */}
      <Modal
        title="Record Actual"
        open={addActualOpen}
        onCancel={() => {
          setAddActualOpen(false);
          addActualForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={addActualForm}
          layout="vertical"
          onFinish={handleAddActual}
        >
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Enter a category' }]}
          >
            <AutoComplete
              placeholder="Type or select category"
              options={catSuggestions}
              filterOption={(input, option) =>
                (option?.value || '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: 'Please enter the amount' }]}
          >
            <InputNumber
              className="w-full"
              min={0}
              step={1000}
              prefix="₹"
              placeholder="Enter amount"
              formatter={(value) =>
                value
                  ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  : ''
              }
              parser={(value) => value.replace(/,/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="actual_date"
            label="Date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>

          <Form.Item name="receipt_url" label="Receipt URL">
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Button
              onClick={() => {
                setAddActualOpen(false);
                addActualForm.resetFields();
              }}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={addActualSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 border-none"
            >
              Record Actual
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* ============================================================= */}
      {/* Edit Actual Modal */}
      {/* ============================================================= */}
      <Modal
        title="Edit Actual"
        open={editActualOpen}
        onCancel={() => {
          setEditActualOpen(false);
          editActualForm.resetFields();
          setEditingActual(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={editActualForm}
          layout="vertical"
          onFinish={handleEditActual}
        >
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Enter a category' }]}
          >
            <AutoComplete
              placeholder="Type or select category"
              options={catSuggestions}
              filterOption={(input, option) =>
                (option?.value || '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: 'Please enter the amount' }]}
          >
            <InputNumber
              className="w-full"
              min={0}
              step={1000}
              prefix="₹"
              placeholder="Enter amount"
              formatter={(value) =>
                value
                  ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  : ''
              }
              parser={(value) => value.replace(/,/g, '')}
            />
          </Form.Item>

          <Form.Item
            name="actual_date"
            label="Date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker className="w-full" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>

          <Form.Item name="receipt_url" label="Receipt URL">
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item className="mb-0 text-right">
            <Button
              onClick={() => {
                setEditActualOpen(false);
                editActualForm.resetFields();
                setEditingActual(null);
              }}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={editActualSubmitting}
              className="bg-indigo-600 hover:bg-indigo-700 border-none"
            >
              Update Actual
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BudgetActualsTab;
